import { type FC, useContext } from 'react';
import { BadgeCheck, Truck } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  item: {
    id: 'inputCart.item',
    defaultMessage: 'Item',
    description: 'Table heading for the ordered product column.',
  },
  amount: {
    id: 'inputCart.amount',
    defaultMessage: 'Amount',
    description: 'Table heading for the money column of an order line.',
  },
  total: {
    id: 'inputCart.total',
    defaultMessage: 'Total',
    description: 'Label before the order total including delivery.',
  },
});

interface CartLine {
  item: string;
  quantity: string;
  unitPrice: string;
  lineTotal: string;
  why?: string;
}

function readLines(value: unknown): CartLine[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const item = str(record.item);
    return item
      ? [
          {
            item,
            quantity: str(record.quantity),
            unitPrice: str(record.unitPrice),
            lineTotal: str(record.lineTotal),
            why: optStr(record.why),
          },
        ]
      : [];
  });
}

export const InputCart: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const surface = useContext(A2uiSurfaceContext);

  const heading = str(node.props.heading);
  const lines = readLines(node.props.lines);
  const deliveryLine = optStr(node.props.deliveryLine);
  const total = str(node.props.total);
  const waivedNote = optStr(node.props.waivedNote);
  const confirmLabel = str(node.props.confirmLabel);
  const changeLabel = optStr(node.props.changeLabel);

  return (
    <div className="w-full rounded-lg border border-border bg-card p-5">
      <h2 className="mb-4 text-balance text-2xl font-bold leading-tight tracking-tight text-foreground">
        {heading}
      </h2>

      <div className="mb-4 flex items-baseline justify-between gap-4 border-b border-border pb-2 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
        <span>{intl.formatMessage(messages.item)}</span>
        <span>{intl.formatMessage(messages.amount)}</span>
      </div>

      <ul className="mb-4 space-y-3">
        {lines.map((line) => (
          <li key={line.item} className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-base font-semibold leading-snug text-foreground" translate="no">
                {line.item}
              </p>
              <p className="text-sm tabular-nums text-muted-foreground">
                {line.quantity} × {line.unitPrice}
              </p>
              {line.why ? (
                <p className="text-sm leading-snug text-muted-foreground-subtle">{line.why}</p>
              ) : null}
            </div>
            <p className="shrink-0 text-base font-bold tabular-nums text-foreground">
              {line.lineTotal}
            </p>
          </li>
        ))}
      </ul>

      {deliveryLine ? (
        <p className="mb-2 flex items-center justify-between gap-4 border-t border-border pt-3 text-sm text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1.5">
            <Truck className="size-4 shrink-0" aria-hidden />
            <span className="min-w-0">{deliveryLine}</span>
          </span>
        </p>
      ) : null}

      {waivedNote ? (
        <p className="mb-2 flex items-start gap-2 rounded-md border border-success/40 bg-success/10 p-3 text-sm font-semibold text-foreground">
          <BadgeCheck className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
          <span className="min-w-0">{waivedNote}</span>
        </p>
      ) : null}

      <div className="mb-5 flex items-baseline justify-between gap-4 border-t-2 border-border pt-3">
        <span className="text-base font-bold uppercase tracking-caps text-muted-foreground">
          {intl.formatMessage(messages.total)}
        </span>
        <span className="text-2xl font-bold tabular-nums tracking-tight text-foreground">
          {total}
        </span>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => surface?.dispatch('confirmOrder', { total })}
          style={{ touchAction: 'manipulation' }}
          className="w-full rounded-md bg-primary px-5 py-3.5 text-base font-bold text-primary-foreground transition-transform duration-150 ease-out hover:shadow-lift focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-95 sm:w-auto sm:flex-1"
        >
          {confirmLabel}
        </button>
        {changeLabel ? (
          <button
            type="button"
            onClick={() => surface?.dispatch('changeOrder', {})}
            style={{ touchAction: 'manipulation' }}
            className="w-full rounded-md border border-border bg-muted px-5 py-3.5 text-base font-semibold text-foreground transition-colors duration-150 ease-out hover:bg-accent-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-95 sm:w-auto"
          >
            {changeLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
};
